import type { PostHog } from "posthog-js";
import type { AnalyticsClient } from "./AnalyticsClient";

// ── the seam's construction half (ported from enderlane's END-170/PHA work) ─
// Runtime gate: with no PostHog key resolved (from /config.json — see
// useSiteConfig) the app must never construct a real analytics client. This
// file is the ONLY place posthog-js may be imported (a fs-scan guard,
// posthogJsImportGuard.spec.ts, scans the rest of src/** for a direct import
// and fails if it finds one). createAnalyticsClient(env) answers null when
// no key is configured (or the key is the empty string).
//
// The `import type` above is compile-time only — it is erased entirely (no
// JS emitted, so the browser never fetches posthog-js for it). The actual
// module is loaded via a runtime `import()` INSIDE the branch that already
// knows a real key is configured, so "no key → no vendor SDK" holds at the
// network layer too: with no key, this file never causes the vendor SDK's
// code to be requested at all, not merely "constructed but idle".
//
// CONSENT (this app diverges from the enderlane reference here — see
// src/_providers/CookieConsentProvider.tsx and analytics.ts's call site in
// AppWithConfig.tsx): enderlane calls this unconditionally the moment a key
// is configured, because its only user is the product's own owner. This app
// has real paying customers AND public guest-facing RSVP pages that no one
// has logged into, so a key being configured is NOT sufficient permission to
// start tracking — the caller (AppWithConfig) additionally gates the call to
// initAnalytics() on the visitor having accepted the cookie-consent banner.
// This module has no opinion about consent at all; it only answers "is there
// a key", exactly as before. Keeping the gate at the CALL SITE rather than
// here means: with consent declined, initAnalytics() is never called, so
// createAnalyticsClient() never runs, so posthog-js is never imported and no
// PostHog network request happens — not merely "constructed but not
// recording".

export interface AnalyticsClientEnv {
  posthogKey?: string;
  posthogHost?: string;
}

class PostHogAnalyticsClient implements AnalyticsClient {
  private posthog: PostHog | null = null;
  private queue: { event: string; props?: Record<string, unknown> }[] = [];

  constructor(key: string, host: string | undefined) {
    void import("posthog-js").then((mod) => {
      const instance = mod.default;
      // MAXIMUM COLLECTION, DELIBERATELY — this is the same data-collection
      // posture enderlane ships (autocapture, pageviews, session replay with
      // masked inputs). That posture is a business decision already made,
      // not this port's to second-guess; what this port changes is WHEN it
      // is allowed to start (see the consent note above), not what it
      // collects once started.
      //
      // Each flag, and what it costs:
      //  * persistence — writes a cookie + localStorage so a session can be
      //    stitched together across page loads. Session replay NEEDS a
      //    durable session id, so replay and memory-only persistence are
      //    mutually exclusive. This is exactly the class of thing consent
      //    must gate, and it is now gated: this constructor only ever runs
      //    after the visitor has accepted.
      //  * autocapture — every click, input change and page view, with
      //    element text. Broad and noisy by design; that is the point.
      //  * capture_pageview — SPA route changes as $pageview.
      //  * session recording — a replayable DOM recording. posthog-js masks
      //    input values by default (maskAllInputs), which is left ON and
      //    MUST stay on: this app's public RSVP forms and payment flows are
      //    typed input from guests who never logged in, and recording an
      //    unmasked keystroke there would put a real person's data in a
      //    third party's store.
      //
      // Server side must agree: session recording only produces data when
      // the PROJECT has it enabled in PostHog (Settings → Session replay). A
      // false there silently yields no recordings while this code looks
      // correct.
      instance.init(key, {
        api_host: host,
        persistence: "localStorage+cookie",
        autocapture: true,
        capture_pageview: true,
        capture_pageleave: true,
        disable_session_recording: false,
        session_recording: {
          maskAllInputs: true,
        },
      });
      this.posthog = instance;
      const queued = this.queue;
      this.queue = [];
      for (const { event, props } of queued) {
        instance.capture(event, props);
      }
    });
  }

  capture(event: string, props?: Record<string, unknown>): void {
    // The dynamic import above may still be in flight (module fetch is
    // async) — queue rather than drop, so an early capture site is never
    // silently lost.
    if (this.posthog) {
      this.posthog.capture(event, props);
      return;
    }
    this.queue.push({ event, props });
  }
}

export function createAnalyticsClient(env: AnalyticsClientEnv): AnalyticsClient | null {
  const key = env.posthogKey;
  if (key === undefined || key === "") return null;

  return new PostHogAnalyticsClient(key, env.posthogHost);
}
